<template>
  <div>
    <a-input-search
      v-model.lazy="search"
      auto-focus
      style="margin-bottom: 14px"
      :placeholder="$t('_.search')"
      @change="handleSearch"
    />
    <a-spin
      v-if="loading"
      style="margin: 7px 0; width: 100%"
    />
    <div v-else>
      <a-tree
        :tree-data="directoryTree"
        :default-expanded-keys="defaultExpanded"
        :expanded-keys="expandedKeys"
        :selected-keys="selectedKeys"
        auto-expand-parent
        show-icon
        @select="handleSelect"
        @expand="handleExpand"
      >
        <a-icon
          slot="folder"
          type="folder"
        />
        <template
          slot="title"
          slot-scope="{ title }"
        >
          <span v-if="title.toLowerCase().indexOf(searchLower) > -1">
            {{ title.substring(0, title.toLowerCase().indexOf(searchLower)) }}
            <span class="search-hit">{{ search }}</span>
            {{ title.substring(title.toLowerCase().indexOf(searchLower) + searchLower.length) }}
          </span>
          <span v-else>{{ title }}</span>
        </template>
      </a-tree>
    </div>
  </div>
</template>

<script>
import { apiGet } from '@/api'
import { apiLink } from '@/utils'
import store from '@/store'
import { get } from 'lodash'
import FileEntity from '@/api/files/fileEntity'

export default {
  name: 'FileTree',
  props: {
    value: {
      type: [Array, Object],
      default: () => [],
    },
    module: {
      type: String,
      default: 'files',
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    selectedPaths: {
      type: Array,
      default: () => [],
    },
  },
  data () {
    return {
      directories: [],
      loading: false,
      search: null,
      expandedKeys: [],
    }
  },
  computed: {
    defaultExpanded () {
      return this.outValue.map(dir => dir.virtual_path)
    },
    directoryTree () {
      const nested = [
        {
          children: [],
          key: '/',
          title: 'Home',
        },
      ]
      this.directories.forEach(dir => {
        if (!dir) return
        const path = dir.virtual_path
          .split('/')

        let treePath = [0]
        if (dir.virtual_path !== '/') {
          let currentPath = path.splice(0, 1)[0]
          while (path.length > 0) {
            const currentPathItem = path.splice(0, 1)[0]
            currentPath += (currentPath !== '/' ? '/' : '') + currentPathItem
            const currentTreeNode = get(nested, treePath)
            let childIndex = currentTreeNode.children
              .findIndex(child => child.title === currentPathItem)

            if (childIndex < 0) {
              childIndex = currentTreeNode.children.push({
                title: currentPathItem,
                key: currentPath,
                children: [],
                slots: {
                  icon: 'folder',
                },
              }) - 1
            }
            treePath.push('children', childIndex)
          }
        }
        const parent = get(nested, treePath)
        const dirKey = dir.fullPath
        const childIndex = parent.children
          .findIndex(child => child.key === dirKey)
        if (childIndex < 0) {
          parent.children.push({
            title: dir.name,
            key: dirKey,
            children: [],
            slots: {
              icon: 'folder',
            },
          })
        }
      })
      return nested[0].children
    },
    outValue () {
      let out = this.value
      if (!Array.isArray(this.value)) {
        out = [out]
      }
      return out.filter(value => value)
    },
    requestUrl () {
      const url = new URL(
        apiLink(
          ['modules', this.module, 'resources'],
          store.state.currentSystem
        )
      )
      const urlParams = {
        page: 1,
        results: -1,
        sortField: 'name',
        sortOrder: 'asc',
      }
      urlParams.pre_filters = [
        {
          field: 'directory',
          operator: '=',
          value: true,
        },
      ]
      urlParams.pre_filters = JSON.stringify(urlParams.pre_filters)
      Object.keys(urlParams).forEach(key => url.searchParams.append(key, urlParams[key]))
      return url
    },
    searchLower () {
      return this.search?.toLowerCase()
    },
    selectedKeys () {
      return this.outValue.map(file => file.fullPath)
    },
  },
  watch: {
    selectedPaths (val) {
      this.expandedKeys.push(...val)
      this.handleSelect(val)
    },
  },
  mounted () {
    // If there are already selected paths when mounted, expand the corresponding keys
    if (this.selectedPaths.length > 0) {
      this.expandedKeys.push(...this.selectedPaths)
    }
    this.getDirectories()
  },
  methods: {
    getDirectories () {
      this.loading = true
      return apiGet(this.requestUrl)
        .then(response => response.json())
        .then(response => {
          if (response.data === undefined) {
            this.directories = []
          } else {
            this.directories = this.directories.concat(response.data.map(file => new FileEntity(file)))
          }
        })
        .catch(e => {
          console.error(e)
          this.$message.error(this.$t('_.something_went_wrong'))
        })
        .finally(() => {
          this.loading = false
        })
    },
    handleExpand (value, expanded, node) {
      this.expandedKeys = value
      if (node) {
        node.slots.icon = expanded ? 'folder-open' : 'folder'
      }
    },
    handleSearch () {
      if (this.search === '') return
      this.expandedKeys = this.directories
        .filter(dir => dir.name.toLowerCase().includes(this.search))
        .map(dir => dir.fullPath)
    },
    handleSelect (value) {
      let out = this.directories.filter(dir => value.indexOf(dir.fullPath) !== -1)

      if (!this.multiple) {
        out = out[0]
      }
      this.$emit('input', out)
    },
  },
}
</script>

<style scoped>
.search-hit {
  color: #e30000;
}
</style>
